/*
 * NextGenn engine · the product page's two small elements.
 *
 *   <ngs-refill-clock>   writes the WhatsApp reminder's date from the
 *                        customer's own calendar (snippets/ngs-refill-clock).
 *   <ngs-sticky-add>     the Add bar at the foot of a phone
 *                        (sections/ngs-product-bar).
 *
 * Both are dressed-up HTML that already works: the clock's link is whole as
 * Liquid wrote it, and the bar is simply never shown without this file. The
 * snippet and the section each ask for this file, so it can arrive twice on one
 * page, hence the guards.
 */
(function () {
  /* ── the refill clock ───────────────────────────────────────────────────── */
  if (!customElements.get('ngs-refill-clock')) {
    customElements.define(
      'ngs-refill-clock',
      class NGSRefillClock extends HTMLElement {
        connectedCallback() {
          var link = this.querySelector('[data-ngs-remind]');
          var weeks = parseInt(this.dataset.weeks, 10);
          if (!link || !weeks) return;

          var number = (this.dataset.number || '').replace(/\D/g, '');
          if (!number) {
            /* The same setting, read off the WhatsApp button the layout renders. */
            var button = document.querySelector('a.ngs-wa[href*="wa.me/"]');
            var found = button && button.getAttribute('href').match(/wa\.me\/(\d+)/);
            number = found ? found[1] : '';
          }
          if (!number) {
            link.hidden = true;
            return;
          }

          var at = new Date();
          at.setDate(at.getDate() + weeks * 7);
          var text = (this.dataset.message || '')
            .replace('[name]', this.dataset.name || '')
            .replace('[date]', this.written(at));
          link.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);
          link.hidden = false;
        }

        /* "27 October": a date a person reads, in the page's language. */
        written(at) {
          var language = document.documentElement.lang || 'en';
          if (language === 'ar') language = 'ar-LB';
          try {
            return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long' }).format(at);
          } catch (error) {
            return at.toDateString();
          }
        }
      }
    );
  }

  /* ── the Add bar ────────────────────────────────────────────────────────── */
  if (!customElements.get('ngs-sticky-add')) {
    customElements.define(
      'ngs-sticky-add',
      class NGSStickyAdd extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.form = document.querySelector('product-info form[data-type="add-to-cart-form"]');
          this.button = this.querySelector('[data-ngs-bar-add]');
          if (!this.form || !this.button) return;
          this.ready = true;

          this.input = this.form.querySelector('input[name="id"]');
          this.submit = this.form.querySelector('[name="add"]');
          this.status = this.querySelector('[data-ngs-status]');
          this.variants = this.read();

          this.onVariant = this.follow.bind(this);
          this.onAdd = this.add.bind(this);
          this.onResize = this.measure.bind(this);
          if (this.input) this.input.addEventListener('change', this.onVariant);
          this.button.addEventListener('click', this.onAdd);
          window.addEventListener('resize', this.onResize);
          if (document.querySelector('cart-drawer')) this.button.setAttribute('aria-haspopup', 'dialog');

          this.passed = false;
          this.atFoot = false;
          this.foot = document.querySelector('.shopify-section-group-footer-group, footer');

          /* AN OBSERVER ONLY REPORTS A CROSSING, AND A PHONE DOES NOT ALWAYS
             CROSS. Jump from above the Add button to below it — a link to an
             anchor, the back button, Safari restoring where you were, a tap on
             "Ask" in the menu — and `isIntersecting` is false at both ends, so
             no callback is ever delivered and the bar stays down for the rest
             of the visit with the real button far above. That is the whole
             reason the bar could not be found on a phone.

             So the rects are the authority and are read on every scroll (once
             a frame, passively); the observer stays because it is the cheap
             path and catches the slow crossings without waking the main
             thread. Both end in the same `gauge`. */
          if ('IntersectionObserver' in window && this.submit) {
            this.watcher = new IntersectionObserver(this.see.bind(this));
            this.watcher.observe(this.submit);
            if (this.foot) this.watcher.observe(this.foot);
          }
          this.onScroll = this.scrolled.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });

          this.follow();
          this.gauge();
        }

        disconnectedCallback() {
          if (!this.ready) return;
          this.ready = false;
          if (this.watcher) this.watcher.disconnect();
          if (this.input) this.input.removeEventListener('change', this.onVariant);
          this.button.removeEventListener('click', this.onAdd);
          window.removeEventListener('resize', this.onResize);
          window.removeEventListener('scroll', this.onScroll);
          this.show(false);
        }

        read() {
          var data = this.querySelector('[data-ngs-bar-variants]');
          try {
            return data ? JSON.parse(data.textContent) : [];
          } catch (error) {
            return [];
          }
        }

        /* Up once the real button has gone off the TOP of the screen (before that the
           customer has not met the sizes and shades yet), down again at the footer. */
        see() {
          this.gauge();
        }

        /* Once a frame at most: a scroll handler that measures on every event
           is how a phone loses its smoothness. */
        scrolled() {
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(
            function () {
              this.ticking = false;
              this.gauge();
            }.bind(this)
          );
        }

        /* THE STATE, READ FROM THE PAGE ITSELF rather than remembered from the
           last crossing — so it is right however the visitor arrived here. */
        gauge() {
          if (!this.ready || !this.submit) return;
          /* Re-read every frame the page moves: the preview bar is injected
             after the page has loaded, so it is rarely there at connect. */
          this.dodge();
          var seen = window.innerHeight || document.documentElement.clientHeight;
          var button = this.submit.getBoundingClientRect();
          /* Up once the real button has gone off the TOP of the screen: before
             that the customer has not met the sizes and shades yet. */
          this.passed = button.bottom <= 0;
          if (this.foot) {
            var foot = this.foot.getBoundingClientRect();
            this.atFoot = foot.top < seen && foot.bottom > 0;
          }
          this.show(this.passed && !this.atFoot);
        }

        show(up) {
          this.classList.toggle('is-up', up);
          document.body.classList.toggle('ngs-bar-up', up);
          if (up) this.measure();
          else {
            /* and put it back, or the column stays lifted over a bar that has
               gone — the dock is a live sum, not a one-way trip */
            document.documentElement.style.removeProperty('--ngs-bar-height');
            document.body.style.removeProperty('--ngs-bar-height');
          }
        }

        measure() {
          this.dodge();
          if (!this.classList.contains('is-up')) return;
          /* What the WhatsApp disc is lifted by: this bar AND anything it has
             itself been lifted over, or the disc lands back on the preview bar. */
          /* ON THE ROOT AS WELL AS THE BODY. `--ngs-dock` is declared on :root
             (ngs-engine.css), and a variable set on <body> does not reach its
             own parent — so published only here, the dock read the bar as 0
             and the whole column stayed put while the bar came up over it. */
          var px = this.offsetHeight + this.preview() + 'px';
          document.documentElement.style.setProperty('--ngs-bar-height', px);
          document.body.style.setProperty('--ngs-bar-height', px);
        }

        /* SHOPIFY'S PREVIEW BAR, MEASURED. An unpublished theme is shown with
           a 68 px bar pinned to the foot of the screen, and it is on top: the
           Add button sat under it and every tap on a preview went to Shopify
           rather than to the shop. Nobody sees this on the published store,
           where there is no preview bar and this is 0. */
        preview() {
          var bar =
            document.querySelector('#PBarNextFrameWrapper') ||
            document.querySelector('#preview-bar-iframe') ||
            document.querySelector('#PBarFrameWrapper');
          if (!bar) return 0;
          var box = bar.getBoundingClientRect();
          var seen = window.innerHeight || document.documentElement.clientHeight;
          /* Only a bar actually sitting at the foot of the screen displaces
             this one — a preview bar somebody has scrolled away is not in the
             way, and neither is one that is hidden. */
          if (!box.height || box.bottom < seen - 2) return 0;
          return Math.round(box.height);
        }

        dodge() {
          /* Both, for the same reason as the bar height above: the dock is
             declared on :root and cannot see a variable set on <body>. */
          var px = this.preview() + 'px';
          document.documentElement.style.setProperty('--ngs-preview-bar', px);
          document.body.style.setProperty('--ngs-preview-bar', px);
        }

        /* Dawn writes the chosen variant into the form and fires `change` on it. */
        follow() {
          var id = this.input ? String(this.input.value) : '';
          var variant = this.variants.filter(function (each) {
            return String(each.id) === id;
          })[0];
          var name = this.querySelector('[data-ngs-bar-variant]');
          var price = this.querySelector('[data-ngs-bar-price]');
          var sells = Boolean(variant && variant.available);

          if (variant && name) name.textContent = variant.title;
          if (variant && price) price.textContent = variant.price;
          this.button.disabled = !sells;
          this.button.textContent = sells ? this.dataset.add || '' : this.dataset.soldOut || '';
          this.say('');
          this.measure();
        }

        add() {
          var id = this.input && this.input.value;
          if (!id || this.button.getAttribute('aria-disabled') === 'true') return;
          /* No bag helper (the file did not arrive): the real button knows what to do. */
          if (!window.NGSCart) {
            if (this.submit) this.submit.click();
            return;
          }

          this.say('');
          this.button.setAttribute('aria-disabled', 'true');
          this.classList.add('is-adding');
          window.NGSCart.add([{ id: id, quantity: 1 }])
            .catch(
              function (error) {
                this.say(error.response ? error.message : this.dataset.error || error.message);
              }.bind(this)
            )
            .finally(
              function () {
                this.button.removeAttribute('aria-disabled');
                this.classList.remove('is-adding');
              }.bind(this)
            );
        }

        say(words) {
          if (!this.status) return;
          this.status.textContent = words;
          this.status.hidden = !words;
          this.measure();
        }
      }
    );
  }
})();

/*
 * NextGenn engine · which pictures fill the frame, and which stand in it.
 *
 * A cut-out is a tall narrow object on nothing and must stand inside the frame
 * with air round it; a photograph is a rectangle and must fill it. The
 * stylesheet used to tell them apart by file extension, which was true of the
 * first three pictures of every bottle and false of every one added since: the
 * shade photographs and the thirty new scenes are PNG, so they were treated as
 * cut-outs and sat small and uneven in the middle of the frame.
 *
 * AND SHAPE IS NOT THE TEST EITHER. It held while every cut-out was a tall
 * slim bottle at about a third of its height — and then The Almond Balm, whose
 * cut-out is a squat JAR very slightly WIDER than it is tall, was read as a
 * photograph, blown up to cover the frame, and cropped on all four sides. No
 * threshold can separate a 1.0 jar from a 0.75 photograph; they overlap.
 *
 * So ask the pixels, which know. A cut-out has nothing in its corners and a
 * photograph does. The picture is fetched at sixteen pixels wide through
 * Shopify's own resizer — about 700 bytes, and the CDN answers
 * `access-control-allow-origin: *`, so the bytes can actually be read — then
 * decoded and its four corners sampled for transparency. It cannot be fooled
 * by a shape or a file name, and a picture uploaded next year is judged the
 * same way.
 *
 * Shape survives underneath as the answer of last resort, for a browser with
 * no `createImageBitmap` or a CDN that one day stops allowing the read. And if
 * this file never runs at all, the stylesheet's extension rule is what is
 * left, which is where this started.
 */
(function () {
  var STAND = 0.62; /* the last resort only: the slim bottles are ~0.34 */
  var CLEAR = 24;   /* alpha at or under this is "nothing there" */
  var asked = Object.create(null); /* one fetch per picture, however many slides show it */

  /* Are this picture's four corners empty? true = a cut-out, false = a
     photograph, null = could not look, so fall back to shape. */
  function hollow(url) {
    if (asked[url]) return asked[url];
    var tiny = url.replace(/([?&])width=\d+/, '$1width=16');
    if (tiny === url) tiny += (url.indexOf('?') < 0 ? '?' : '&') + 'width=16';
    asked[url] =
      typeof createImageBitmap !== 'function' || typeof fetch !== 'function'
        ? Promise.resolve(null)
        : fetch(tiny, { mode: 'cors', credentials: 'omit' })
            .then(function (r) { return r.ok ? r.blob() : Promise.reject(); })
            .then(function (blob) { return createImageBitmap(blob); })
            .then(function (bm) {
              var c = document.createElement('canvas');
              c.width = bm.width;
              c.height = bm.height;
              var ctx = c.getContext('2d', { willReadFrequently: true });
              ctx.drawImage(bm, 0, 0);
              var d = ctx.getImageData(0, 0, bm.width, bm.height).data;
              var w = bm.width, h = bm.height;
              if (bm.close) bm.close();
              function alphaAt(x, y) { return d[(y * w + x) * 4 + 3]; }
              return (
                alphaAt(0, 0) <= CLEAR &&
                alphaAt(w - 1, 0) <= CLEAR &&
                alphaAt(0, h - 1) <= CLEAR &&
                alphaAt(w - 1, h - 1) <= CLEAR
              );
            })
            .catch(function () { return null; });
    return asked[url];
  }

  function judge(img) {
    if (!img || img.dataset.ngsFit || img.dataset.ngsAsked) return;
    var url = img.currentSrc || img.src;
    if (!url) return; /* a lazy slide with no source yet — the load listener will call again */
    img.dataset.ngsAsked = '1';
    hollow(url).then(function (empty) {
      if (empty === null) {
        if (img.naturalWidth && img.naturalHeight) {
          img.dataset.ngsFit = img.naturalWidth / img.naturalHeight >= STAND ? 'fill' : 'stand';
        }
        return;
      }
      img.dataset.ngsFit = empty ? 'stand' : 'fill';
    });
  }

  function sweep() {
    var pictures = document.querySelectorAll('.product__media img, .product .thumbnail img');
    Array.prototype.forEach.call(pictures, function (img) {
      judge(img);
      /* `complete` IS TRUE FOR AN IMAGE WITH NO SOURCE YET, which is every lazy
         slide below the first: judged at that moment it has no dimensions to
         judge by, and without a listener it is never looked at again. So the
         listener goes on regardless of what `complete` says, and `judge` is
         cheap and refuses to run twice. */
      if (!img.dataset.ngsAsked && !img.dataset.ngsWatched) {
        img.dataset.ngsWatched = '1';
        img.addEventListener('load', function () { judge(img); });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  else sweep();
  /* The gallery is a slider and the later slides arrive lazily; the variant
     picker swaps pictures too. Both are cheap to re-sweep. */
  window.addEventListener('load', sweep);
  document.addEventListener('shopify:section:load', sweep);
  if ('MutationObserver' in window) {
    var media = document.querySelector('.product__media-list, media-gallery');
    if (media) new MutationObserver(sweep).observe(media, { childList: true, subtree: true });
  }
})();
